import type { CustomCaseType } from "@/features/customCaseTypes/types";
import type { ForeignNational } from "./foreignNational";
import type { DividerTabTextSize, PaginationPosition } from "./packet";

/**
 * A firm's exhibit title template over the title fields (`{doc_type}`,
 * `{subject}`, `{author}`, `{issuer}`, `{date}`, `{identifier}`, `{qualifier}`,
 * `{form}`, `{notice}`): `default` for general documents, `by_type` per
 * document type or family (`letter`, `uscis_notice`).
 */
export type TitleTemplate = {
  default?: string;
  by_type?: Record<string, string>;
};

/**
 * The firm's packet defaults as the backend validates them (its packet
 * `DividerConfig` / `PaginationConfig`): what a case's packet uses while its
 * own settings are still CaseBlink's.
 */
/** Which work products a new packet includes; omitted keys keep CaseBlink's include. */
export type FirmPacketContents = {
  cover_letter?: boolean;
  forms?: boolean;
  letters?: boolean;
  exhibit_list?: boolean;
  exhibits?: boolean;
  other?: boolean;
};

export type FirmPacketSettings = {
  divider: {
    mainGroupEnabled: boolean;
    categoryEnabled: boolean;
    documentEnabled: boolean;
    /** "#RRGGBB". */
    color: string;
    textSize: DividerTabTextSize;
  };
  pagination: {
    enabled: boolean;
    position: PaginationPosition;
    startOn: "firstPage";
  };
  contents?: FirmPacketContents;
  /** The order new packets list the work product kinds in; omitted for CaseBlink's. */
  contents_order?: (keyof FirmPacketContents)[];
};

/**
 * Settings > Case Preparation's firm-level text and packet defaults: each
 * key as the law firm record names it. Admin-only, saved through
 * `PUT /law_firms/<id>`; null (or "" for a text) clears it. Kept apart from
 * Firm Instructions (`agents-md`), which is the firm's own text. Rename a
 * key here and every reader and writer follows.
 */
export const CASE_PREP_FIELDS = {
  /** Free-form exhibit title style. */
  titleInstructions: "title_instructions",
  /** Free-form folder structure preferences. */
  folderInstructions: "folder_instructions",
  /** Free-form filing packet preferences. */
  packetInstructions: "packet_instructions",
  packetSettings: "default_packet_settings",
  /** What Case Insights does on its own, on every case. */
  caseInsightsAutomation: "case_insights_automation"
} as const;

/** The backend's cap on each text, after trimming. */
export const CASE_PREP_TEXT_MAX = 4000;

export type CasePrepKey = keyof typeof CASE_PREP_FIELDS;

type CasePrepValues = {
  titleInstructions: string | null;
  folderInstructions: string | null;
  packetInstructions: string | null;
  packetSettings: FirmPacketSettings | null;
  caseInsightsAutomation: Partial<CaseInsightsAutomation> | null;
};

/** The firm's Case Insights switches; the backend fills in its defaults. */
export type CaseInsightsAutomation = {
  /** Recompute insights when documents or case details change. */
  keep_current: boolean;
  /** Write research memos without being asked. */
  run_analyses: boolean;
  /** Read the petitioner's website when the petitioner changes. */
  petitioner_web: boolean;
};

export type CasePrepFirmFields = {
  [K in CasePrepKey as (typeof CASE_PREP_FIELDS)[K]]?: CasePrepValues[K];
};

/**
 * The backend sends the key, null included. An older backend omits it and
 * drops it on save, so nothing may look saved there.
 */
export const hasCasePrepField = (
  firm: object | null | undefined,
  key: CasePrepKey
): boolean => Boolean(firm) && CASE_PREP_FIELDS[key] in (firm as object);

export const casePrepValue = <K extends CasePrepKey>(
  firm: CasePrepFirmFields | null | undefined,
  key: K
): CasePrepValues[K] | null =>
  ((firm as Record<string, unknown> | null | undefined)?.[
    CASE_PREP_FIELDS[key]
  ] as CasePrepValues[K] | undefined) ?? null;

/** The `PUT /law_firms/<id>` body that saves `value` (null clears). */
export const casePrepPatch = <K extends CasePrepKey>(
  key: K,
  value: CasePrepValues[K]
): CasePrepFirmFields =>
  ({ [CASE_PREP_FIELDS[key]]: value } as CasePrepFirmFields);

export type EnterpriseProviderType = "saml" | "oidc";

export interface EnterpriseAuthProvider {
  provider_id: string;
  type: EnterpriseProviderType | string;
  label?: string | null;
  enabled?: boolean | null;
}

/**
 * The client events that may wake the assistant in the client's channel
 * (mirrors `agent_event_service.EVENT_KINDS` on the server).
 */
export type AgentEventKind =
  | "case_status"
  | "intake_submitted"
  | "vault_upload"
  | "expiration";

/** The USCIS forms' unit checkbox: apartment, suite or floor. */
export type UnitType = "apt" | "ste" | "flr";

/** A mailing address split the way USCIS forms ask for it. */
export interface OfficeAddress {
  street?: string | null;
  unit_type?: UnitType | null;
  unit_number?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  province?: string | null;
  postal_code?: string | null;
  country?: string | null;
}

export interface BarAdmission {
  licensing_authority: string;
  bar_number?: string | null;
}

/** What an attorney or accredited representative puts on a G-28 (and the
 * preparer section of other forms); the form fills read it. */
export interface RepresentativeProfile {
  uscis_online_account_number?: string | null;
  office_address?: OfficeAddress | null;
  daytime_phone?: string | null;
  mobile_phone?: string | null;
  email?: string | null;
  fax?: string | null;
  bar_admissions?: BarAdmission[];
  eoir_id?: string | null;
  subject_to_restricting_order?: boolean | null;
  restricting_order_explanation?: string | null;
  is_accredited_representative?: boolean | null;
  recognized_organization?: string | null;
  accreditation_date?: string | null;
  is_law_student?: boolean | null;
  supervising_attorney?: string | null;
}

/**
 * How prose cites an exhibit (`ExhibitScheme.citation_style`): "(Ex. 12, p. 3)"
 * is prefix "Ex.", pincite ", p. " and `print_page`.
 */
export type CitationStyle = {
  prefix?: string | null;
  print_page?: boolean;
  pincite?: string;
  separator?: string;
  source?: string | null;
};

export type CitationStyleProposal = {
  style: CitationStyle;
  /** "(Ex. 12, p. 3)" */
  example: string;
  /** The playbook line it was read from. */
  rule?: string | null;
  source_entry_id?: string | null;
  source_name?: string | null;
  at?: string | null;
};

export interface LawFirm extends CasePrepFirmFields {
  uid: string;
  name: string;
  owner_id?: string | null;
  stripe_customer_id?: string | null;
  latest_subscription_id?: string | null;
  parent_law_firm_id?: string | null;
  credit_balance?: number | null;
  lawyers_count?: number | null;
  firm_size?: FirmSize | null;
  source?: Source | null;
  address?: string | null;
  unit_type?: UnitType | null;
  unit_number?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  province?: string | null;
  postal_code?: string | null;
  country?: string | null;
  phone?: string | null;
  fax?: string | null;
  website?: string | null;
  logo_url?: string | null;
  /** The firm's IANA timezone; Casey's "today" and deadlines use it. */
  timezone?: string | null;
  enable_whitelabel_foreign_national_intake?: boolean | null;
  /** Classic drafting allowlist. Only a superadmin changes it, through its own route. */
  legacy_drafting_enabled?: boolean | null;
  legacy_drafting_enabled_at?: string | null;
  legacy_drafting_enabled_by?: string | null;
  /** Settings > Case Preparation > Custom Case Types, in Settings order. */
  custom_case_types?: CustomCaseType[];

  // Agent approvals (Settings > Agents). Reversible changes run and can be
  // undone unless the firm asks to be asked before every change; the event
  // wakes say which client events start a proposing run of the assistant.
  agent_ask_every_write?: boolean | null;
  /** Minutes a question waits before going with its Recommended answers,
   * where the attorney said not to ask; null is off. */
  agent_auto_answer_minutes?: number | null;
  agent_event_wakes?: Partial<Record<AgentEventKind, boolean>> | null;
  /** Sidebar tab ids (`SIDEBAR_TABS`) hidden for every member; empty shows all. */
  hidden_sidebar_tabs?: string[] | null;
  // Exhibit house style: the scheme new cases start with and the template
  // titles are composed with. Null is the built-in style.
  default_exhibit_scheme?: Record<string, unknown> | null;
  default_title_template?: TitleTemplate | null;
  /** A citation style read off a playbook, until an admin saves or dismisses it. */
  citation_style_proposal?: CitationStyleProposal | null;
  created_at?: string | null;
  updated_at?: string | null;
  deleted_at?: string | null;
  is_deleted?: boolean | null;

  // Enterprise SSO (Identity Platform)
  sso_enabled?: boolean | null;
  enforce_sso?: boolean | null;
  identity_platform_tenant_id?: string | null;
  allowed_email_domains?: string[] | null;
  enterprise_providers?: EnterpriseAuthProvider[] | null;

  // Optional denormalized subscription fields (present only if backend includes them)
  latest_subscription?: Subscription | null;
  latest_subscription_plan?: SubscriptionPlan | null;
  subscription?: Subscription | null;
  subscription_plan?: SubscriptionPlan | null;
  subscription_status?: SubscriptionStatus | null;
  subscription_plan_type?: SubscriptionPlanType | null;
}

export enum SubscriptionStatus {
  ACTIVE = "active",
  PAUSED = "paused",
  CANCELLED = "cancelled",
  EXPIRED = "expired"
}

export enum SubscriptionPlanType {
  FREE_TRIAL = "Free Trial",
  LITE = "Lite",
  SOLO_SMALL_FIRM = "Solo / Small Firm",
  TIER_ONE = "Tier 1",
  TIER_TWO = "Tier 2",
  TIER_THREE = "Tier 3",
  LEGACY = "Legacy"
}

export enum FirmSize {
  ONE = "1",
  TWO_TO_FIVE = "2-5",
  SIX_TO_TEN = "6-10",
  ELEVEN_TO_FIFTY = "11-50",
  FIFTY_ONE_TO_ONE_HUNDRED = "51-100",
  ONE_HUNDRED_ONE_TO_FIVE_HUNDRED = "101-500",
  FIVE_HUNDRED_PLUS = "500+"
}

export enum Source {
  EMAIL_OUTREACH = "Email Outreach",
  AILA_ADVERTISEMENT = "AILA Advertisement",
  AILA_CONFERENCE = "AILA Conference",
  AILA_EMAIL = "AILA Email",
  FRIEND = "Friend",
  REFERRAL = "Referral",
  LINKEDIN = "LinkedIn",
  GOOGLE = "Google",
  PERSONAL_CONNECTION = "Personal Connection",
  OTHER = "Other"
}

export interface Subscription {
  uid: string;
  law_firm_id: string;
  plan_id: string;
  subscription_status: SubscriptionStatus;
  start_date?: string | null;
  end_date?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface SubscriptionPlan {
  uid: string;
  subscription_plan_type: SubscriptionPlanType;
  credit_allowance: number;
  stripe_product_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface SubscriptionWithPlanDTO {
  subscription: Subscription;
  subscription_plan?: SubscriptionPlan | null;
}

export interface CreateLawFirmSubscriptionRequest {
  plan_id?: string | null;
  plan?: CreateSubscriptionPlanRequest | null;
  subscription_status: SubscriptionStatus;
  start_date?: string | null;
  end_date?: string | null;
}

export interface CreateLawFirmSubscriptionResponse {
  lawFirm: LawFirm;
  subscription: Subscription;
  subscriptionPlan: SubscriptionPlan | null;
}

export interface UpdateSubscriptionRequest {
  plan_id?: string | null;
  subscription_status?: SubscriptionStatus | null;
  start_date?: string | null;
  end_date?: string | null;
}

export interface CreateSubscriptionPlanRequest {
  subscription_plan_type: SubscriptionPlanType;
  credit_allowance?: number;
  stripe_product_id?: string | null;
}

export interface CreateInitialSubscriptionRequest {
  plan_id?: string | null;
  plan?: CreateSubscriptionPlanRequest | null;
  subscription_status?: SubscriptionStatus;
  start_date?: string | null;
  end_date?: string | null;
}

export interface CreateLawFirmRequest {
  name: string;
  owner_id?: string | null;
  stripe_customer_id?: string | null;
  parent_law_firm_id?: string | null;
  credit_balance?: number;
  firm_size?: FirmSize | null;
  source?: Source | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  phone?: string | null;
  website?: string | null;
  initial_subscription?: CreateInitialSubscriptionRequest | null;
}

export interface UpdateLawFirmRequest extends CasePrepFirmFields {
  name?: string | null;
  owner_id?: string | null;
  stripe_customer_id?: string | null;
  latest_subscription_id?: string | null;
  parent_law_firm_id?: string | null;
  credit_balance?: number | null;
  firm_size?: FirmSize | null;
  source?: Source | null;
  address?: string | null;
  unit_type?: UnitType | null;
  unit_number?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  province?: string | null;
  postal_code?: string | null;
  country?: string | null;
  phone?: string | null;
  fax?: string | null;
  website?: string | null;
  /** Owners and admins; an IANA name like America/New_York. */
  timezone?: string | null;
  enable_whitelabel_foreign_national_intake?: boolean | null;
  agent_ask_every_write?: boolean | null;
  /** Owners and admins; 1 to 240, null turns it off. */
  agent_auto_answer_minutes?: number | null;
  /** The whole map; the server replaces what it holds. */
  agent_event_wakes?: Partial<Record<AgentEventKind, boolean>> | null;
  /** Owners and admins; the whole list, in any order. Hiding every tab is refused. */
  hidden_sidebar_tabs?: string[] | null;
  /** Owners and admins; null resets to the built-in style. */
  default_exhibit_scheme?: Record<string, unknown> | null;
  default_title_template?: TitleTemplate | string | null;
  /** Only ever cleared: saved (on the scheme) or dismissed. */
  citation_style_proposal?: null;

  // Enterprise SSO (Identity Platform)
  sso_enabled?: boolean | null;
  enforce_sso?: boolean | null;
  identity_platform_tenant_id?: string | null;
  allowed_email_domains?: string[] | null;
  enterprise_providers?: EnterpriseAuthProvider[] | null;
}

export interface AdminListLawFirmsParams {
  includeDeleted?: boolean;
  orderBy?: string;
  descending?: boolean;
  limit?: number;
  pageToken?: string;
  q?: string;
}

export interface AdminListLawFirmsResponse {
  lawFirms: LawFirm[];
  nextPageToken?: string | null;
  totalCount?: number;
}

export type LawFirmForeignNationalSort =
  | "newest"
  | "oldest"
  | "firstNameAsc"
  | "firstNameDesc"
  | "lastNameAsc"
  | "lastNameDesc";

export interface ListLawFirmForeignNationalsParams {
  limit?: number;
  offset?: number;
  q?: string;
  sort?: LawFirmForeignNationalSort;
}

export interface ListLawFirmForeignNationalsResponse {
  foreign_nationals: ForeignNational[];
  total_count: number;
  assigned_count: number;
}

export interface CreateLawFirmLawyerRequest {
  email: string;
  password?: string;
  randomizePassword?: boolean;
  firstName: string;
  lastName: string;
  middleName?: string | null;
  title?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  phone?: string | null;
  role?: LawyerRole | string | null;
}

export interface CreateLawFirmInviteRequest {
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  title?: string | null;
  role?: LawyerRole | string | null;
}

export interface LawFirmInviteDTO {
  uid: string;
  law_firm_id: string;
  email: string;
  role?: LawyerRole | string | null;
  firstName?: string | null;
  lastName?: string | null;
  title?: string | null;
  accepted_by_uid?: string | null;
  accepted_at?: unknown;
  created_at?: string | null;
  updated_at?: string | null;
}

/**
 * The firm's standing instructions for its agents (Settings > Case
 * Preparation > "Firm Instructions"). One free-text document; `text` is null
 * when the firm has written nothing. Wire path: `/law_firms/<id>/agents-md`.
 */
export interface FirmAgentInstructionsDTO {
  law_firm_id: string | null;
  text: string | null;
  updated_at: string | null;
  updated_by: string | null;
  updated_by_name: string | null;
  max_chars: number;
  /** What the editor loaded; sent back with a save so a stale one is refused. */
  version?: string;
}

/** What a firm may grant per role in Settings > Permissions. */
export type FirmCapability = "library_write" | "skills_write";

/** The roles whose permissions a firm may switch; owners and admins hold everything. */
export type FirmConfigurableRole = "attorney" | "paralegal";

export type FirmPermissionMatrix = Record<
  FirmCapability,
  Record<FirmConfigurableRole, boolean>
>;

/**
 * Who besides owners and admins may write Firm Knowledge, plus what the
 * caller may do (`mine`). Wire path: `/law_firms/<id>/permissions`.
 */
export interface FirmPermissionsDTO {
  law_firm_id: string | null;
  capabilities: Array<{
    key: FirmCapability;
    label: string;
    description: string;
  }>;
  roles: Array<{ key: FirmConfigurableRole; label: string }>;
  matrix: FirmPermissionMatrix;
  mine: Record<FirmCapability, boolean>;
  updated_at: string | null;
  updated_by: string | null;
  updated_by_name: string | null;
}

export type SsoProviderType = "oidc" | "saml";

export interface SsoSetupStateDTO {
  lawFirmId: string;
  lawFirmName: string;
  ssoEnabled: boolean;
  enforceSso: boolean;
  tenantId?: string | null;
  allowedEmailDomains: string[];
  providers: Array<{
    provider_id: string;
    type: SsoProviderType | string;
    label?: string | null;
    enabled?: boolean | null;
  }>;
}

export interface CreateTenantResponseDTO {
  tenantId: string;
}

export interface UpdateSsoAllowedEmailDomainsRequestDTO {
  allowed_email_domains: string[];
}

export interface UpsertSsoProviderRequestDTO {
  provider_id: string;
  type: SsoProviderType;
  label?: string | null;
  enabled?: boolean;
  config: Record<string, unknown>;
}

export interface ProvisionUsersRequestDTO {
  dry_run: boolean;
  limit?: number;
}

export interface ProvisionUsersResponseDTO {
  dry_run: boolean;
  tenantId: string;
  total: number;
  results: Array<Record<string, unknown>>;
}

export interface ProviderConfigsResponseDTO {
  tenantId: string;
  configs: SsoProviderConfigDTO[];
}

export interface SsoProviderConfigDTO {
  provider_id: string;
  type: SsoProviderType | string;
  displayName?: string | null;
  enabled?: boolean | null;
  issuer?: string | null;
  clientId?: string | null;
  idpConfig?: Record<string, unknown> | null;
  spConfig?: Record<string, unknown> | null;
  idpEntityId?: string | null;
  ssoUrl?: string | null;
  spEntityId?: string | null;
  callbackUri?: string | null;
  error?: string | null;
}

export interface SamlMetadataImportRequestDTO {
  metadata_url: string;
  binding_preference?: "redirect" | "post";
}

export interface SamlMetadataImportResponseDTO {
  idpEntityId: string;
  ssoUrl: string;
  x509Certificate?: string | null;
}

export type ValidateSsoProviderCheckDTO = {
  name: string;
  ok: boolean;
  details?: string;
};

export interface ValidateSsoProviderRequestDTO {
  provider_id: string;
  provider_type?: SsoProviderType;
  expected_callback_uri?: string;
}

export interface ValidateSsoProviderResponseDTO {
  provider_id: string;
  type: SsoProviderType | string;
  ok: boolean;
  checks: ValidateSsoProviderCheckDTO[];
}

export enum LawyerRole {
  // Deprecated legacy values (kept for backwards compatibility)
  FULL = "full",
  STANDARD = "standard",

  // Current supported roles
  OWNER = "owner",
  ADMIN = "admin",
  ATTORNEY = "attorney",
  PARALEGAL = "paralegal",
  SUPERADMIN = "superadmin"
}

export interface LawyerDTO {
  uid: string;
  email: string;
  firstName: string;
  lastName: string;
  middleName?: string | null;
  title?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  phone?: string | null;
  practiceName?: string | null;
  subscription?: Record<string, unknown> | null;
  disabled?: boolean | null;
  role?: LawyerRole | string | null;
  lawyerRole?: LawyerRole | string | null;
  lawyer_role?: LawyerRole | string | null;
  law_firm_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  lastSignedInAt?: number | null;
  last_signed_in_at?: number | null;
  /** G-28 / preparer details (Settings > Account > Representative details). */
  representative_profile?: RepresentativeProfile | null;
}
